/**
 * Bitcoin / DCA simulation — pure calculation logic.
 *
 * Model: a fixed amount is invested at the start of every month; the asset
 * price compounds at a constant monthly rate i = annualGrowth / 12.
 *   btcBought(m) = monthlyInvestment / price(m)
 *   price(m + 1)  = price(m) × (1 + i)
 *   portfolioValue = Σ btcBought × finalPrice
 *
 * A constant growth rate is a modelling convenience, not a forecast: real
 * prices are path dependent and highly volatile. Results are hypothetical and
 * exclude exchange fees, spreads and taxes.
 */

export const DCA_VERIFIED_AT = "2026-08-15";

export interface DcaYearSnapshot {
  year: number;
  totalInvested: number;
  btcAccumulated: number;
  avgBuyPrice: number;
  btcPrice: number;
  portfolioValue: number;
}

export interface DcaResult {
  totalInvested: number;
  totalBTC: number;
  finalPrice: number;
  avgBuyPrice: number;
  portfolioValue: number;
  profit: number;
  yearSnapshots: DcaYearSnapshot[];
}

export function simulateDca(
  priceToday: number,
  monthlyInvestment: number,
  years: number,
  annualGrowthPct: number,
): DcaResult {
  const months = Math.round(years * 12);
  const i = annualGrowthPct / 100 / 12;

  let price = priceToday;
  let totalBTC = 0;
  let totalInvested = 0;
  const yearSnapshots: DcaYearSnapshot[] = [];

  for (let m = 1; m <= months; m++) {
    if (price > 0) totalBTC += monthlyInvestment / price;
    totalInvested += monthlyInvestment;
    price = price * (1 + i);

    if (m % 12 === 0) {
      yearSnapshots.push({
        year: m / 12,
        totalInvested,
        btcAccumulated: totalBTC,
        avgBuyPrice: totalBTC > 0 ? totalInvested / totalBTC : 0,
        btcPrice: price,
        portfolioValue: totalBTC * price,
      });
    }
  }

  return {
    yearSnapshots,
    totalInvested,
    totalBTC,
    finalPrice: price,
    avgBuyPrice: totalBTC > 0 ? totalInvested / totalBTC : 0,
    portfolioValue: totalBTC * price,
    profit: totalBTC * price - totalInvested,
  };
}

export interface DcaScenario {
  key: "negative" | "flat" | "positive";
  label: string;
  /** Annual price growth assumed in this scenario, in percent. */
  growthPct: number;
  finalPrice: number;
  totalInvested: number;
  totalBTC: number;
  value: number;
  profit: number;
  description: string;
}

/**
 * Three explicit outcomes, each a full re-simulation of the same monthly plan:
 * a negative scenario (price falls), a zero scenario (price flat throughout)
 * and a positive scenario (the growth rate chosen by the user).
 * All figures are hypothetical and exclude exchange fees, spreads and taxes.
 */
export function dcaScenarios(
  priceToday: number,
  monthlyInvestment: number,
  years: number,
  positiveGrowthPct: number,
  negativeGrowthPct = -20,
): DcaScenario[] {
  const build = (
    key: DcaScenario["key"],
    label: string,
    growthPct: number,
    description: string,
  ): DcaScenario => {
    const sim = simulateDca(priceToday, monthlyInvestment, years, growthPct);
    return {
      key,
      label,
      growthPct,
      finalPrice: sim.finalPrice,
      totalInvested: sim.totalInvested,
      totalBTC: sim.totalBTC,
      value: sim.portfolioValue,
      profit: sim.profit,
      description,
    };
  };

  return [
    build(
      "negative",
      "Отрицателен сценарий",
      negativeGrowthPct,
      `Цената пада с ${Math.abs(negativeGrowthPct)}% годишно — портфейлът завършва на загуба.`,
    ),
    build(
      "flat",
      "Нулев сценарий",
      0,
      "Цената е непроменена през целия период — стойността е равна на вложеното преди такси.",
    ),
    build(
      "positive",
      "Положителен сценарий",
      positiveGrowthPct,
      `Цената расте с ${positiveGrowthPct}% годишно според избраното от вас допускане.`,
    ),
  ];
}

